import React, { useEffect, useRef } from 'react';
import {
  BrowserRouter, Outlet, Route, Routes, matchPath, useLocation, useNavigationType, useOutletContext, useParams,
} from 'react-router-dom';
import { trackPageView } from './analytics';
import AdminRoute from './components/AdminRoute';
import SiteLayout from './components/SiteLayout';
import { getPageKind } from './siteLocale';

// Code-split each route's page component into its own chunk instead of
// bundling hero-hub + blog + admin-CMS (react-quill/codemirror and all) into
// one main.js that every visitor downloads regardless of which area they
// actually landed on. See the comment above <Suspense> in SiteLayout.jsx for
// where the loading fallback while a chunk downloads is rendered.
const App = React.lazy(() => import('./App'));
const BlogLandingPage = React.lazy(() => import('./pages/BlogLandingPage'));
const ArticlePage = React.lazy(() => import('./pages/ArticlePage'));
const AuthorPage = React.lazy(() => import('./pages/AuthorPage'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const AdminArticlesPage = React.lazy(() => import('./pages/AdminArticlesPage'));
const AdminArticleEditorPage = React.lazy(() => import('./pages/AdminArticleEditorPage'));
const AdminCategoriesPage = React.lazy(() => import('./pages/AdminCategoriesPage'));
const AdminAuthorsPage = React.lazy(() => import('./pages/AdminAuthorsPage'));
const AdminFeedbackPage = React.lazy(() => import('./pages/AdminFeedbackPage'));
const AdminNewsPage = React.lazy(() => import('./pages/AdminNewsPage'));
const AdminImagesPage = React.lazy(() => import('./pages/AdminImagesPage'));
const AdminWritePage = React.lazy(() => import('./pages/AdminWritePage'));

// Article pages send their own page view once the article has loaded and set
// its title (see ArticlePage); sent from here it would always carry the
// generic placeholder title of the loading state.
const SELF_TRACKED_ROUTES = ['/blog/:articleSlug', '/:lang/blog/:articleSlug'];
const isSelfTrackedRoute = (pathname) => SELF_TRACKED_ROUTES.some((pattern) => matchPath(pattern, pathname));

// A new page starts at the top. Back/forward (POP) is left alone so the
// browser can put you back where you were. Only the path counts: the hub
// rewrites ?hero= while you browse it, which must not jump the page. Picking
// a blog category changes the path too, but that's the same list being
// filtered, so it keeps your place.
const isBlogList = (pathname) => ['blog', 'category'].includes(getPageKind(pathname));

function ScrollToTopOnNavigate() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const previousPathRef = useRef(pathname);

  useEffect(() => {
    const previous = previousPathRef.current;
    previousPathRef.current = pathname;
    if (navigationType === 'POP') return;
    if (isBlogList(previous) && isBlogList(pathname)) return;
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return null;
}

function RouteAnalytics() {
  const location = useLocation();

  // Keyed on pathname only: the hero hub rewrites ?hero= every time a hero is
  // opened (tracked separately as hero_open), which shouldn't count as a page view.
  useEffect(() => {
    if (isSelfTrackedRoute(location.pathname)) return;
    trackPageView(`${location.pathname}${location.search}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}

// "/:lang/..." only exists for es and pt: English has no prefix, so /en/blog
// or /fr/blog is a 404, as the server answers it. The layout's context (lang,
// session) passes through to the pages.
function LocaleGate() {
  const { lang } = useParams();
  const context = useOutletContext();
  return ['es', 'pt'].includes(lang) ? <Outlet context={context} /> : <NotFoundPage />;
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <ScrollToTopOnNavigate />
      <RouteAnalytics />
      <Routes>
        <Route element={<SiteLayout />}>
          <Route path="/" element={<App />} />
          {/* Spanish and Portuguese hub; App reads the language from the layout. */}
          <Route path="/es" element={<App />} />
          <Route path="/pt" element={<App />} />
          <Route path="/blog" element={<BlogLandingPage />} />
          <Route path="/blog/category/:categorySlug" element={<BlogLandingPage />} />
          <Route path="/blog/author/:authorId" element={<AuthorPage />} />
          <Route path="/blog/:articleSlug" element={<ArticlePage />} />
          <Route path=":lang" element={<LocaleGate />}>
            <Route path="blog" element={<BlogLandingPage />} />
            <Route path="blog/category/:categorySlug" element={<BlogLandingPage />} />
            <Route path="blog/author/:authorId" element={<AuthorPage />} />
            <Route path="blog/:articleSlug" element={<ArticlePage />} />
          </Route>
          <Route path="/about" element={<AboutPage />} />
          <Route path="/es/about" element={<AboutPage />} />
          <Route path="/pt/about" element={<AboutPage />} />
          <Route element={<AdminRoute />}>
            <Route path="/admin/articles" element={<AdminArticlesPage />} />
            <Route path="/admin/articles/new" element={<AdminArticleEditorPage />} />
            <Route path="/admin/articles/:articleId/edit" element={<AdminArticleEditorPage />} />
            <Route path="/admin/categories" element={<AdminCategoriesPage />} />
            <Route path="/admin/authors" element={<AdminAuthorsPage />} />
            <Route path="/admin/feedback" element={<AdminFeedbackPage />} />
            <Route path="/admin/news" element={<AdminNewsPage />} />
            <Route path="/admin/images" element={<AdminImagesPage />} />
            <Route path="/admin/write" element={<AdminWritePage />} />
            <Route path="/admin/write/:sessionId" element={<AdminWritePage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
